import * as Api from '@ViewModels';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const AUTOMATION_REPORTS_QUERY_KEY = 'automationReports';

interface UseInfiniteAutomationReportsQueryParams {
	resourceSelectorId: Api.ResourceSelectorId;
	pageSize?: number;
	startDate?: Date;
	endDate?: Date;
	userId?: string;
	policies?: string[];
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

const getApiPath = (resourceSelectorId: Api.ResourceSelectorId): string => {
	switch (resourceSelectorId) {
		case Api.ResourceSelectorId.PolicyRenew: {
			return 'reports/automation/policyrenew';
		}
		case Api.ResourceSelectorId.NewClientAutomationsOnHold: {
			return 'reports/automation/newclient';
		}
		case Api.ResourceSelectorId.NewDonorAutomationsOnHold: {
			return 'reports/automation/newdonor';
		}
		case Api.ResourceSelectorId.NewLeadAutomationsOnHold: {
			return 'reports/automation/newlead';
		}
		default: {
			return 'reports/automation/ResourceSelector';
		}
	}
};

const buildRequest = (
	resourceSelectorId: Api.ResourceSelectorId,
	startDate?: Date,
	endDate?: Date,
	userId?: string,
	policies?: string[]
): Api.IAutomationReportRequest | Api.IRenewalAutomationReportRequest => {
	const baseRequest: Api.IAutomationReportRequest = {
		endDate: endDate?.toISOString(),
		resourceSelectorId,
		startDate: startDate?.toISOString(),
		userId,
	};

	switch (resourceSelectorId) {
		case Api.ResourceSelectorId.PolicyRenew: {
			const renewalRequest: Partial<Api.IRenewalAutomationReportRequest> = {
				endDate: baseRequest.endDate,
				linesOfBusiness: policies || [],
				startDate: baseRequest.startDate,
				userId: baseRequest.userId,
			};
			return renewalRequest as Api.IRenewalAutomationReportRequest;
		}
		case Api.ResourceSelectorId.NewClientAutomationsOnHold:
		case Api.ResourceSelectorId.NewDonorAutomationsOnHold:
		case Api.ResourceSelectorId.NewLeadAutomationsOnHold: {
			return {
				endDate: baseRequest.endDate,
				startDate: baseRequest.startDate,
				userId: baseRequest.userId,
			} as Api.IAutomationReportRequest;
		}
		default: {
			return baseRequest;
		}
	}
};

export const useInfiniteAutomationReportsQuery = ({
	resourceSelectorId,
	pageSize = 25,
	startDate,
	endDate,
	userId,
	policies,
	enabled = true,
	impersonationContext,
}: UseInfiniteAutomationReportsQueryParams) => {
	const userSession = useUserSession();

	return useInfiniteQuery<Api.IPagedCollection<Api.IAutomationReportView>>({
		enabled,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			const apiPath = getApiPath(resourceSelectorId);
			const request = buildRequest(resourceSelectorId, startDate, endDate, userId, policies);

			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IAutomationReportView>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { pageSize, pageToken: pageParam },
					urlPath: apiPath,
					impersonationContext,
				}),
				'POST',
				request
			);
		},
		queryKey: [
			AUTOMATION_REPORTS_QUERY_KEY,
			{
				endDate: endDate?.toISOString(),
				pageSize,
				policies,
				resourceSelectorId,
				startDate: startDate?.toISOString(),
				userId,
				impersonationContext: impersonationContext?.account?.id
					? {
							accountId: impersonationContext.account.id,
							userId: impersonationContext.user?.id,
						}
					: undefined,
			},
		],
		initialPageParam: '',
	});
};

export const invalidateAutomationReports = () => {
	reactQueryClient.invalidateQueries({ queryKey: [AUTOMATION_REPORTS_QUERY_KEY] });
};
